import type { ReportCoverImageRequest } from '@client/helpers/report-cover-image-status.helpers';
import { Baseline, IElementID } from '@shared/models/project.model';
import { PickKeyUnion } from '@shared/models/type-helpers.model';

export enum Page {
  Elements = 'elements',
  Diff = 'diff',
  Report = 'report',
  Products = 'products',
  Calculations = 'calculations',
  DataTables = 'data-tables',
  Design = 'design',
  Import = 'imports',
}

/** Design import started from the profile Admin Area. */
export type DesignImportRequest = 'none' | 'pdf' | 'geometry-ai';

type ClearSelectionExcludeType = PickKeyUnion<
  IUIState,
  'addedElementId' | 'addedPropertyId'
>;

export type IUIState = {
  selectedPage: Page;
  setSelectedPage: (page: Page) => void;

  observedElementIds: Record<IElementID, boolean>;
  addObservedElementIds: (
    observedElementIds: Record<IElementID, boolean>,
  ) => void;

  addedElementId?: string;
  setAddedElementId: (addedElementId?: string) => void;

  addedPropertyId?: string;
  setAddedPropertyId: (addedPropertyId?: string) => void;

  selectedElementId?: string;
  setSelectedElementId: (id?: string) => void;

  selectedVersionId?: Baseline['id'];
  setSelectedVersionId: (id?: string) => void;

  recentlyUndoRedoElementId?: IElementID;
  setRecentlyUndoRedoElementId: (id?: IElementID) => void;

  /**
   * Id of the element or version in inline-rename. Also used to open rename on a
   * just-created version. Not synced from the URL (unlike `addedElementId`).
   */
  renamingId?: string;
  setRenamingId: (id?: string) => void;

  selectedProjectFolderId?: string;
  setSelectedProjectFolderId: (id?: string) => void;

  /**
   * Clear the added element and property markers, except for the ones in exclude
   */
  clearSelections: (...exclude: ClearSelectionExcludeType[]) => void;

  /**
   * Clear one selection
   */
  clearSelection: (target: ClearSelectionExcludeType) => void;

  /** Controls visibility of the render debug panel (admin only). */
  showRenderDebug: boolean;
  setShowRenderDebug: (show: boolean) => void;

  /**
   * Admin Area request to create or remove the report cover image.
   * Consumed by the report-view controller; stays set until handled or cancelled.
   */
  reportCoverImageRequest: ReportCoverImageRequest;
  setReportCoverImageRequest: (request: ReportCoverImageRequest) => void;

  /**
   * Admin Area request to start a Design import (PDF underlay or AI geometry).
   * Consumed by the Design map; stays set until handled.
   */
  designImportRequest: DesignImportRequest;
  setDesignImportRequest: (request: DesignImportRequest) => void;
};
